import { useNavigate } from '@tanstack/react-router';
import type { CityDto, SourceChannelDto, SourceMessageStatus } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ImportQueueFiltersState {
  status?: SourceMessageStatus | undefined;
  cityId?: string | undefined;
  channelId?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

interface Props {
  cities: ReadonlyArray<CityDto>;
  channels: ReadonlyArray<SourceChannelDto>;
  value: ImportQueueFiltersState;
}

const ANY = '__any__';

const STATUSES: SourceMessageStatus[] = [
  'PENDING',
  'EXTRACTED',
  'IGNORED',
  'IMPORTED',
  'REJECTED',
  'DUPLICATE',
];

export function ImportQueueFilters({ cities, channels, value }: Props) {
  const { t } = useTranslation('import-queue');
  const navigate = useNavigate();
  const [local, setLocal] = useState<ImportQueueFiltersState>(value);

  // Sync from URL when user navigates back/forward.
  useEffect(() => {
    setLocal(value);
  }, [value]);

  // `value` is the URL-committed state; `patch` overrides individual keys.
  // We never merge with `local` here — that avoids stale-closure bugs in
  // uncontrolled inputs (e.g. date pickers whose onChange fires before React
  // flushes the previous setState).
  const apply = (patch: Partial<ImportQueueFiltersState>) => {
    const next = { ...value, ...patch };
    setLocal(next);
    navigate({
      to: '/import-queue',
      search: () => {
        // Cursor resets on filter change — never propagate it.
        const out: Record<string, string> = {};
        for (const [k, v] of Object.entries(next)) {
          if (v !== undefined && v !== '' && v !== null) out[k] = String(v);
        }
        return out;
      },
    });
  };

  const reset = () => {
    setLocal({});
    navigate({ to: '/import-queue', search: () => ({}) });
  };

  const hasAny = Object.values(local).some((v) => v !== undefined && v !== '');

  // If a city is selected, narrow channel list to that city.
  const visibleChannels = local.cityId ? channels.filter((c) => c.cityId === local.cityId) : channels;

  return (
    <div className="border rounded-md p-4 mb-4 grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-5">
      <div>
        <Label className="text-xs">{t('filters.statusLabel')}</Label>
        <Select
          value={local.status ?? ANY}
          onValueChange={(v) => apply({ status: v === ANY ? undefined : (v as SourceMessageStatus) })}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('filters.any')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t('filters.any')}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`status.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">{t('filters.cityLabel')}</Label>
        <Select
          value={local.cityId ?? ANY}
          onValueChange={(v) =>
            apply({
              cityId: v === ANY ? undefined : v,
              // City change can invalidate the selected channel — reset it.
              channelId: undefined,
            })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder={t('filters.any')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t('filters.any')}</SelectItem>
            {cities.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">{t('filters.channelLabel')}</Label>
        <Select
          value={local.channelId ?? ANY}
          onValueChange={(v) => apply({ channelId: v === ANY ? undefined : v })}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('filters.any')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t('filters.any')}</SelectItem>
            {visibleChannels.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                @{c.slug}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="from" className="text-xs">
          {t('filters.fromLabel')}
        </Label>
        <Input
          id="from"
          type="date"
          value={local.from ?? ''}
          onChange={(e) => {
            const val = e.target.value || undefined;
            setLocal((prev) => ({ ...prev, from: val }));
            apply({ from: val });
          }}
        />
      </div>

      <div>
        <Label htmlFor="to" className="text-xs">
          {t('filters.toLabel')}
        </Label>
        <Input
          id="to"
          type="date"
          value={local.to ?? ''}
          onChange={(e) => {
            const val = e.target.value || undefined;
            setLocal((prev) => ({ ...prev, to: val }));
            apply({ to: val });
          }}
        />
      </div>

      {hasAny && (
        <div className="md:col-span-3 lg:col-span-5">
          <Button variant="ghost" size="sm" onClick={reset}>
            {t('filters.reset')}
          </Button>
        </div>
      )}
    </div>
  );
}
